import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { RouterModule } from "@angular/router";
import { Report, ReportsService } from "@hydrantid/acm-client";
import { createEmbeddingContext, EmbeddingContext, FrameOptions } from "amazon-quicksight-embedding-sdk";
import { BehaviorSubject, firstValueFrom, ReplaySubject } from "rxjs";
import { map } from "rxjs/operators";
import { CachedDataService } from "../../services/cached-data/cached-data.service";
import { Store } from "@ngrx/store";
import { AppUser, AppUserSelectors } from "../../modules/shared/app-user";
import {
    NotificationBannerComponent,
    NotificationBannerSeverity,
} from "../../modules/shared/notification-banner/notification-banner.component";
import { LetDirective } from "@ngrx/component";
import { BackButtonComponent } from "../../modules/shared/back-button/back-button.component";

@Component({
    selector: "app-report-embed",
    templateUrl: "./report-embed.component.html",
    standalone: true,
    imports: [BackButtonComponent, CommonModule, LetDirective, NotificationBannerComponent, RouterModule],
})
export class ReportEmbedComponent implements OnInit {
    showList$ = new ReplaySubject<boolean>(1);
    pageTitle$ = new ReplaySubject<string>(1);
    reports$ = new ReplaySubject<Report[]>(1);
    error$ = new BehaviorSubject<string | undefined>(undefined);
    passedAccountId$ = this.activatedRoute.queryParams.pipe(map((params) => params.accountId));

    protected readonly NotificationBannerSeverity = NotificationBannerSeverity;

    constructor(
        private reportsService: ReportsService,
        private activatedRoute: ActivatedRoute,
        private cachedDataService: CachedDataService,
        protected store: Store<{ appUser: AppUser }>,
    ) {}

    ngOnInit(): void {
        this.initializePage();
    }

    async initializePage(): Promise<void> {
        const queryParams = await firstValueFrom(this.activatedRoute.queryParams);
        let accountId = "";
        if (queryParams.accountId) {
            accountId = queryParams.accountId;
        } else {
            // Get user's first account
            const firstAccountId = await firstValueFrom(this.store.select(AppUserSelectors.firstAccountId));
            if (!firstAccountId) {
                this.error$.next("No account found");
                return;
            }
            accountId = firstAccountId;
        }
        const params = await firstValueFrom(this.activatedRoute.params);
        if (params.key) {
            this.showList$.next(false);
            await this.loadReport(params.key, accountId);
        } else {
            this.showList$.next(true);
            this.pageTitle$.next("Reports");
            await this.loadReportList(accountId);
        }
    }

    async loadReportList(accountId: string): Promise<void> {
        try {
            this.reports$.next(await firstValueFrom(this.cachedDataService.getReportsForAccount(accountId)));
            this.error$.next(undefined);
        } catch {
            this.error$.next("Failed to load reports. Please try again.");
        }
    }

    async loadReport(reportId: string, accountId: string): Promise<void> {
        // Give the view a stable title while the account-specific label is loading.
        this.pageTitle$.next("Report");
        try {
            // The embed endpoint supplies a URL, while the cached report list supplies the display label.
            const reports = await firstValueFrom(this.cachedDataService.getReportsForAccount(accountId));
            const report = reports.find((r) => r.id === reportId);
            this.pageTitle$.next(report?.label || "Report");
            const reportUrl = await firstValueFrom(this.reportsService.embedReport(reportId));
            // Await the SDK so its failures are handled by this user-facing load-error boundary.
            await this.embedReport(reportUrl, report?.label?.trim() || "Embedded report");
            this.error$.next(undefined);
        } catch {
            this.error$.next("Failed to load report");
        }
    }

    /* istanbul ignore next */
    async createAWSEmbeddingContext(): Promise<EmbeddingContext> {
        return createEmbeddingContext();
    }
    async embedReport(url: string, reportTitle: string): Promise<void> {
        const embeddingContext = await this.createAWSEmbeddingContext();

        const frameOptions: FrameOptions = {
            url,
            container: "#report",
            height: "100%",
            width: "100%",
            resizeHeightOnSizeChangedEvent: true,
            onChange: (event, metadata) => {
                if (event.eventName === "FRAME_MOUNTED" && metadata?.frame) {
                    metadata.frame.title = reportTitle;
                }
            },
        };

        const contentOptions = {
            toolbarOptions: {
                export: false,
                undoRedo: false,
                reset: false,
            },
            attributionOptions: {
                overlayContent: false,
            },
        };
        await embeddingContext.embedDashboard(frameOptions, contentOptions);
    }
}
